import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

export interface VoteCounts {
  total_votes: number
  up_count: number
  down_count: number
  native_speaker_approved: boolean
  native_speaker_flagged: boolean
}

export type VoteValue = 'up' | 'down' | null

function getSessionId(): string {
  let id = localStorage.getItem('neptam_session_id')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('neptam_session_id', id)
  }
  return id
}

const ZERO_COUNTS: VoteCounts = {
  total_votes: 0,
  up_count: 0,
  down_count: 0,
  native_speaker_approved: false,
  native_speaker_flagged: false,
}

// ── Single-entry hook (EntryPage) ─────────────────────────────

interface UseVotesResult {
  counts: VoteCounts
  myVote: VoteValue
  loading: boolean
  castVote: (v: 'up' | 'down') => Promise<void>
}

export function useVotes(entryId: string | undefined): UseVotesResult {
  const [counts, setCounts] = useState<VoteCounts>(ZERO_COUNTS)
  const [myVote, setMyVote]   = useState<VoteValue>(null)
  const [loading, setLoading] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return
      supabase.rpc('is_admin').then(({ data }) => setIsAdmin(!!data))
    })
  }, [])

  useEffect(() => {
    if (!entryId) return
    let cancelled = false

    const load = async () => {
      setLoading(true)
      const sessionId = getSessionId()

      const [countsRes, myVoteRes] = await Promise.all([
        supabase
          .from('vote_counts')
          .select('total_votes,up_count,down_count,native_speaker_approved,native_speaker_flagged')
          .eq('entry_id', entryId)
          .maybeSingle(),
        supabase
          .from('votes')
          .select('vote')
          .eq('entry_id', entryId)
          .eq('session_id', sessionId)
          .maybeSingle(),
      ])

      if (cancelled) return
      setCounts((countsRes.data as VoteCounts) ?? ZERO_COUNTS)
      setMyVote((myVoteRes.data?.vote as VoteValue) ?? null)
      setLoading(false)
    }

    load()
    return () => { cancelled = true }
  }, [entryId])

  const castVote = useCallback(async (v: 'up' | 'down') => {
    if (!entryId) return
    const sessionId = getSessionId()
    const toggling  = myVote === v

    if (toggling) {
      await supabase.from('votes').delete()
        .eq('entry_id', entryId)
        .eq('session_id', sessionId)

      setMyVote(null)
      setCounts(prev => ({
        ...prev,
        total_votes: prev.total_votes - 1,
        up_count:    v === 'up'   ? prev.up_count   - 1 : prev.up_count,
        down_count:  v === 'down' ? prev.down_count - 1 : prev.down_count,
        native_speaker_approved: isAdmin && v === 'up'   ? false : prev.native_speaker_approved,
        native_speaker_flagged:  isAdmin && v === 'down' ? false : prev.native_speaker_flagged,
      }))
    } else {
      const { error } = await supabase.from('votes').upsert(
        { entry_id: entryId, session_id: sessionId, vote: v, is_native_speaker_verified: isAdmin },
        { onConflict: 'entry_id,session_id' }
      )
      if (!error) {
        const prev_vote = myVote
        setMyVote(v)
        setCounts(prev => {
          let up = prev.up_count, down = prev.down_count, total = prev.total_votes
          if (prev_vote === 'up')   { up--;   total-- }
          if (prev_vote === 'down') { down--;  total-- }
          if (v === 'up')   { up++;   total++ }
          if (v === 'down') { down++;  total++ }
          return {
            ...prev, total_votes: total, up_count: up, down_count: down,
            native_speaker_approved: isAdmin ? v === 'up'   : prev.native_speaker_approved,
            native_speaker_flagged:  isAdmin ? v === 'down' : prev.native_speaker_flagged,
          }
        })
      }
    }
  }, [entryId, myVote, isAdmin])

  return { counts, myVote, loading, castVote }
}

// ── Batch hook (SearchPage grid) ──────────────────────────────
// Two queries for the whole page: one for counts, one for the
// session's own votes. castVote updates state optimistically.

export interface BatchVoteState {
  counts:   { [entryId: string]: VoteCounts }
  myVotes:  { [entryId: string]: VoteValue }
  castVote: (entryId: string, v: 'up' | 'down') => Promise<void>
}

export function useBatchVotes(entryIds: string[]): BatchVoteState {
  const [counts,  setCounts]  = useState<{ [id: string]: VoteCounts }>({})
  const [myVotes, setMyVotes] = useState<{ [id: string]: VoteValue }>({})
  const [isAdmin, setIsAdmin] = useState(false)

  // Keep a ref so castVote always reads latest myVotes without re-creating
  const myVotesRef = useRef(myVotes)
  myVotesRef.current = myVotes

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return
      supabase.rpc('is_admin').then(({ data }) => setIsAdmin(!!data))
    })
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = entryIds.join(',')

  useEffect(() => {
    if (entryIds.length === 0) return
    let cancelled = false
    const sessionId = getSessionId()

    Promise.all([
      supabase
        .from('vote_counts')
        .select('entry_id,total_votes,up_count,down_count,native_speaker_approved,native_speaker_flagged')
        .in('entry_id', entryIds),
      supabase
        .from('votes')
        .select('entry_id,vote')
        .in('entry_id', entryIds)
        .eq('session_id', sessionId),
    ]).then(([countsRes, myVotesRes]) => {
      if (cancelled) return
      const countsMap: { [id: string]: VoteCounts } = {}
      for (const row of countsRes.data ?? []) countsMap[row.entry_id] = row as VoteCounts
      setCounts(countsMap)

      const myVotesMap: { [id: string]: VoteValue } = {}
      for (const row of myVotesRes.data ?? []) myVotesMap[row.entry_id] = row.vote as VoteValue
      setMyVotes(myVotesMap)
    })

    return () => { cancelled = true }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  const castVote = useCallback(async (entryId: string, v: 'up' | 'down') => {
    const sessionId   = getSessionId()
    const currentVote = myVotesRef.current[entryId] ?? null
    const toggling    = currentVote === v

    if (toggling) {
      await supabase.from('votes').delete()
        .eq('entry_id', entryId)
        .eq('session_id', sessionId)

      setMyVotes(prev => ({ ...prev, [entryId]: null }))
      setCounts(prev => {
        const e = prev[entryId] ?? ZERO_COUNTS
        return {
          ...prev,
          [entryId]: {
            ...e,
            total_votes: e.total_votes - 1,
            up_count:    v === 'up'   ? e.up_count   - 1 : e.up_count,
            down_count:  v === 'down' ? e.down_count - 1 : e.down_count,
            native_speaker_approved: isAdmin && v === 'up'   ? false : e.native_speaker_approved,
            native_speaker_flagged:  isAdmin && v === 'down' ? false : e.native_speaker_flagged,
          },
        }
      })
    } else {
      const { error } = await supabase.from('votes').upsert(
        { entry_id: entryId, session_id: sessionId, vote: v, is_native_speaker_verified: isAdmin },
        { onConflict: 'entry_id,session_id' }
      )
      if (!error) {
        setMyVotes(prev => ({ ...prev, [entryId]: v }))
        setCounts(prev => {
          const e    = prev[entryId] ?? ZERO_COUNTS
          let up     = e.up_count
          let down   = e.down_count
          let total  = e.total_votes
          if (currentVote === 'up')   { up--;   total-- }
          if (currentVote === 'down') { down--;  total-- }
          if (v === 'up')   { up++;   total++ }
          if (v === 'down') { down++;  total++ }
          return {
            ...prev,
            [entryId]: {
              ...e, total_votes: total, up_count: up, down_count: down,
              native_speaker_approved: isAdmin ? v === 'up'   : e.native_speaker_approved,
              native_speaker_flagged:  isAdmin ? v === 'down' : e.native_speaker_flagged,
            },
          }
        })
      }
    }
  }, [isAdmin])

  return { counts, myVotes, castVote }
}
